import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { API_PATH, RouterLinks } from '../../constants';
import { GlobalQuestionService } from '../../services/global-question.service';
import { SignalRService } from '../../services/signal-r.service';
import { PolicyQuery } from '../../../policy/state/policy.query';
import { UserStore } from '../../../authentication/state/user.store';
import { environment } from 'src/environments/environment';

declare var $: any;
declare const Dropzone: any;
declare const dropzonePreviewTemplate: any;

export interface ParsedMessage {
  heading?: string;
  text?: string;
  days_left?: number;
  actions?: { label: string; url: string; primary?: boolean }[];
}

export interface PriorityOption {
  priority_id: number;
  priority_name: string;
  priority_description: string;
}

export interface NotificationItem {
  id: string;
  threadId: number;
  channelId: number;
  icon: string;
  title: string;
  message: string;
  parsedMessage?: ParsedMessage | null;
  time: string;
  sender: string;
  senderTitle?: string;
  direction: 'inbound' | 'outbound';
  priorityId: number | null;
  priorityName: string;
  policyId?: string | null;
  isRead: boolean;
  plainText: string;
  displayTime: string;
  displayDate: string;
  attachmentUrl?: string;
  attachmentName?: string;
  policyLabel?: string | null;
}

export interface NotificationGroup {
  category: string;
  label: string;
  description?: string;
  items: NotificationItem[];
}

export interface ThreadSummary {
  threadId: number;
  preview: NotificationItem;
  hasUnread: boolean;
  hasAttachment: boolean;
  channels: number[];
  conversationName?: string;
  conversationDescription?: string;
}

export interface ThreadGroup {
  category: string;
  label: string;
  description?: string;
  threads: ThreadSummary[];
}

@Component({
  selector: 'app-notifications-landing',
  templateUrl: './notifications-landing.component.html',
  styleUrls: ['./notifications-landing.component.scss'],
  standalone: false
})
export class NotificationsLandingComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('chatBody') private chatBodyEl?: ElementRef<HTMLElement>;
  @ViewChild('sidecolInner') private sidecolInnerEl?: ElementRef<HTMLElement>;

  private signalRSub: Subscription | null = null;
  private searchSub: Subscription | null = null;
  private searchSubject = new Subject<string>();
  private notifDropzone: any = null;

  RouterLinks = RouterLinks;
  policy: any = null;
  currentUser: any = null;
  isBrokerOrAdmin = false;
  showNewChat = false;
  newChatMessage = '';
  replyActive = false;
  pendingAttachmentUrl: string | null = null;
  notificationGroups: NotificationGroup[] = [];
  selectedNotificationId: string | null = null;
  currentThreadId: number | null = null;
  showOlderMessages = false;
  private _activeCategory = 'all';
  get activeCategory() { return this._activeCategory; }
  set activeCategory(v: string) { this._activeCategory = v; this.recomputeFilteredGroups(); }
  filterCategoryPolicies = false;
  filterCategoryQuotes = false;
  filterCategoryClaims = false;
  filterCategoryPromotions = false;
  filterCategoryChats = false;
  policyFilterAll = true;
  selectedPolicyIds: Record<string, boolean> = {};
  filterDateRange: 'all' | '3m' | '6m' | '1y' = 'all';

  private _filterType: 'all' | 'unread' = 'all';
  get filterType() { return this._filterType; }
  set filterType(v: 'all' | 'unread') { this._filterType = v; }

  priorities: PriorityOption[] = [];
  filterPriorityIds: Record<number, boolean> = {};

  private _searchQuery = '';
  get searchQuery() { return this._searchQuery; }
  set searchQuery(v: string) { this._searchQuery = v; this.searchSubject.next(v); }

  private _filteredGroupsByThread: ThreadGroup[] = [];
  private _selectedThreadMessagesByDate: { date: string; messages: NotificationItem[] }[] = [];
  private _unreadCount = 0;
  private _uniquePolicies: { policyId: string; label: string; subtitle: string; count: number }[] = [];
  private _allThreadCount = 0;
  viewingAttachmentUrl: string | null = null;
  viewingAttachmentDownloadUrl: string | null = null;
  private blobUrl: string | null = null;
  attachmentTitle = '';
  stuckCategory: string | null = null;

  readonly channels = [
    { id: 1, name: 'Email' },
    { id: 2, name: 'Whatsapp' },
    { id: 9, name: 'SMS' },
  ];
  notifyChannels: Record<number, boolean> = {};
  chatSelectValue = '';

  private readonly channelIconMap: Record<number, string> = {
    1: 'envelope-filled',
    2: 'whatsapp-solid',
    9: 'sms-solid',
    3: 'chat',
    8: 'chat',
  };

  get customerName(): string {
    if (this.isBrokerOrAdmin) {
      const person = this.policy?.person?.[0];
      return person ? `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim() || 'Customer' : 'Customer';
    }
    return `${this.currentUser?.first_name ?? ''} ${this.currentUser?.last_name ?? ''}`.trim() || 'Customer';
  }

  get hasNotifications(): boolean {
    return this.notificationGroups.some(g => g.items.length > 0);
  }

  get backDisabled(): boolean {
    return !this.selectedNotificationId && !this.showNewChat && !this.currentThreadId;
  }

  get unreadCount(): number { return this._unreadCount; }

  get filteredGroupsByThread(): ThreadGroup[] {
    return this._filteredGroupsByThread;
  }

  get filteredThreadCount(): number {
    return this._filteredGroupsByThread.reduce((sum, g) => sum + g.threads.length, 0);
  }

  get activeFilterCount(): number {
    const totalPriorities = this.priorities.length;
    const checkedPriorities = Object.values(this.filterPriorityIds).filter(v => v).length;
    const priorityFiltered = totalPriorities > 0 && checkedPriorities < totalPriorities ? 1 : 0;
    return [
      this._filterType !== 'all',
      this.filterCategoryPolicies,
      this.filterCategoryQuotes,
      this.filterCategoryClaims,
      this.filterCategoryPromotions,
      this.filterCategoryChats,
      this.filterDateRange !== 'all',
    ].filter(Boolean).length + priorityFiltered;
  }

  get uniquePolicies(): { policyId: string; label: string; subtitle: string; count: number }[] {
    return this._uniquePolicies;
  }

  get policyFilterLabel(): string {
    if (this.policyFilterAll) return 'All messages';
    const selected = Object.entries(this.selectedPolicyIds).filter(([, v]) => v).map(([k]) => k);
    if (selected.length === 0) return 'All messages';
    if (selected.length > 1) return 'Multiple selected';
    return selected[0];
  }

  get allThreadCount(): number { return this._allThreadCount; }

  get selectedNotification(): NotificationItem | null {
    for (const group of this.notificationGroups) {
      const item = group.items.find(i => i.id === this.selectedNotificationId);
      if (item) return item;
    }
    return null;
  }

  get selectedThreadMessagesByDate(): { date: string; messages: NotificationItem[] }[] {
    return this._selectedThreadMessagesByDate;
  }

  get olderThreadMessages(): { date: string; messages: NotificationItem[] }[] {
    const all = this.selectedThreadMessagesByDate;
    return all.length > 1 ? all.slice(0, all.length - 1) : [];
  }

  get recentThreadMessages(): { date: string; messages: NotificationItem[] }[] {
    const all = this.selectedThreadMessagesByDate;
    return all.length > 0 ? [all[all.length - 1]] : [];
  }

  get isMailchimpThread(): boolean {
    if (!this.currentThreadId) return false;
    for (const g of this.notificationGroups) {
      const found = g.items.find(i => i.threadId === this.currentThreadId);
      if (found) return found.channelId === 6;
    }
    return false;
  }

  getChannelIcon(channelId: number): string {
    return this.channelIconMap[channelId] ?? 'envelope-filled';
  }

  safeId(category: string): string {
    return category.replace(/[^a-zA-Z0-9]/g, '-');
  }

  groupHasUnread(threads: ThreadSummary[]): boolean {
    return threads.some(t => t.hasUnread);
  }

  trackByCategory(_: number, group: { category: string }): string {
    return group.category;
  }

  trackByThread(_: number, thread: { threadId: number }): number {
    return thread.threadId;
  }

  trackByDate(_: number, dateGroup: { date: string }): string {
    return dateGroup.date;
  }

  trackByMessage(_: number, msg: NotificationItem): string {
    return msg.id;
  }

  toPlainText(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  buildActionUrl(url: string, policyId?: string | null): string {
    if (!url) return url;
    const base = policyId ? (url.includes('?') ? `${url}&policy_id=${policyId}` : `${url}?policy_id=${policyId}`) : url;
    const withSource = base.includes('?') ? `${base}&source=notifications` : `${base}?source=notifications`;
    const isLocalhost = window.location.hostname === 'localhost';
    const baseUrl = isLocalhost ? window.location.origin : `${environment.cssURL}central`;
    return `${baseUrl}/${withSource.replace(/^\//, '')}`;
  }

  formatMessageTime(time: string): string {
    if (!time) return '';
    const d = new Date(time);
    if (isNaN(d.getTime())) return time;
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  formatMessageDate(time: string): string {
    if (!time) return '';
    const d = new Date(time);
    if (isNaN(d.getTime())) return time;
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) {
      return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    const isThisYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', ...(!isThisYear && { year: 'numeric' }) });
  }

  constructor(
    private router: Router,
    private http: HttpClient,
    private globalQuestionService: GlobalQuestionService,
    private signalRService: SignalRService,
    private policyQuery: PolicyQuery,
    private userStore: UserStore,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.searchSub = this.searchSubject.pipe(debounceTime(300)).subscribe(() => this.recomputeFilteredGroups());
    this.policy = this.policyQuery.getActive();
    this.currentUser = this.userStore.getValue();
    this.isBrokerOrAdmin = !!(this.currentUser?.console_access || this.currentUser?.broker_id > 0);
    this.globalQuestionService.runCommandNew('comm.usp_get_message_priorities', new Map())
      .subscribe((rows: PriorityOption[]) => {
        this.priorities = rows ?? [];
        this.priorities.forEach(p => this.filterPriorityIds[p.priority_id] = true);
        this.recomputeFilteredGroups();
        this.cdr.detectChanges();
      });
    this.loadNotifications();

    const personId = this.isBrokerOrAdmin
      ? this.policy?.person?.[0]?.person_id
      : this.currentUser?.person_id;
    if (personId) {
      this.signalRService.connect(personId.toString());
      this.signalRSub = this.signalRService.newMessage$
        .pipe(debounceTime(500))
        .subscribe(() => this.loadNotifications());
    }
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.signalRSub?.unsubscribe();
    this.signalRService.disconnect();
    this.notifDropzone?.destroy();
  }

  loadNotifications(): void {
    const user = this.currentUser;
    const personId = this.isBrokerOrAdmin
      ? this.policy?.person[0]?.person_id
      : user?.person_id;
    if (!personId) return;
    //console.log('Loading notifications for person_id:', personId);
    const parameters = new Map<string, string>();
    parameters.set('person_id', personId.toString());
    
   
    this.globalQuestionService.runCommandNew('comm.usp_get_messages_by_person', parameters)
      .subscribe((messages: any[]) => {
        
        console.log(messages);
        if (!messages?.length) {
          this.notificationGroups = [];
          return;
        }
       
        const sorted = [...messages].sort((a, b) => (a.message_id ?? 0) - (b.message_id ?? 0));
        const threadCategoryMap = new Map<number, string>();
        const groupMap = new Map<string, NotificationItem[]>();
        const groupDescriptionMap = new Map<string, string>();

        for (const m of sorted) {
          const ownCategory = m.conversation_name ?? m.channel_name ?? m.category ?? m.thread_type ?? 'general';
          const threadId = m.thread_id ?? 0;

          if (!threadCategoryMap.has(threadId)) {
            threadCategoryMap.set(threadId, ownCategory);
          }
          const category = threadCategoryMap.get(threadId)!;

          if (m.conversation_description && !groupDescriptionMap.has(category)) {
            groupDescriptionMap.set(category, m.conversation_description);
          };

          let parsedMessage: ParsedMessage | null = null;
          try {
            const p = JSON.parse(m.body);
            if (p && typeof p === 'object' && !Array.isArray(p)) parsedMessage = p;
          } catch {}

          const item: NotificationItem = {
            id: m.message_id?.toString(),
            threadId,
            channelId: +(m.source_channel_id ?? 0),
            icon: m.icon ?? 'chat',
            title: m.subject ?? m.title ?? m.channel_name ?? 'Message',
            message: m.body,
            parsedMessage,
            time: m.date_created ?? m.created_at ?? m.sent_at ?? '',
            sender: m.sender_name ?? m.sender ?? '',
            direction: m.direction === 'outbound' ? 'outbound' : 'inbound',
            priorityId: m.priority_id ?? null,
            priorityName: m.priority_name ?? m.priority ?? '',
            policyId: m.policy_id ?? null,
            isRead: this.isBrokerOrAdmin
              ? !!(m.read_by_agent)
              : m.send_status === 'read' || !!(m.read_by_client ?? m.is_read),
            plainText: (m.body ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(),
            displayTime: this.formatMessageTime(m.date_created ?? m.created_at ?? m.sent_at ?? ''),
            displayDate: this.formatMessageDate(m.date_created ?? m.created_at ?? m.sent_at ?? ''),
            attachmentUrl: m.resource_id
              ? `${API_PATH.UPLOAD_RESOURCE}/${m.resource_id}`
              : m.attachment_url
                ? `${API_PATH.UPLOAD_RESOURCE}/${m.attachment_url}`
                : undefined,
            senderTitle: m.sender_title ?? m.sender_role ?? '',
            attachmentName: m.attachment_name ?? m.resource_name ?? '',
            policyLabel: m.policy_name ?? m.policy_description ?? null
          };
          if (!groupMap.has(category)) groupMap.set(category, []);
          groupMap.get(category)!.push(item);
        }

        
        this.notificationGroups = Array.from(groupMap.entries()).map(([category, items]) => ({
          category,
          label: category,
          description: groupDescriptionMap.get(category),
          items
        }));
        
        this.recomputeFilteredGroups();
        this.recomputeSelectedMessages();

        if (this.showNewChat && this.currentThreadId === null) {
          const latest = messages.reduce((max: any, m: any) =>
            (m.message_id ?? 0) > (max?.message_id ?? 0) ? m : max, null);
          if (latest?.thread_id) {
            this.currentThreadId = latest.thread_id;
            this.showNewChat = false;
          }
        }

        if (this.currentThreadId !== null) this.scrollToBottom();
        this.cdr.detectChanges();
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      $('#app-bar-desktop').find('#more').addClass('is-active');
      if (window.matchMedia('(min-width: 992px)').matches && this.hasNotifications) {
        const first = this.notificationGroups[0]?.items[0];
        if (first) this.selectNotification(first);
      }
    }, 200);

    this.notifDropzone = new Dropzone('#notification-dropzone', {
      url: API_PATH.UPLOAD_RESOURCE,
      autoProcessQueue: true,
      autoDiscover: false,
      previewTemplate: dropzonePreviewTemplate,
      createImageThumbnails: false,
      acceptedFiles: 'application/*,.doc,.docx,.xls,.xlsx,.pdf,.txt',
      maxFilesize: 200,
      headers: { Authorization: `Bearer ${this.userStore.getValue()?.access_token}` }
    }).on('success', (_file: any, response: any) => {
      this.pendingAttachmentUrl = response;
      this.replyActive = true;
    }).on('removedfile', () => {
      this.pendingAttachmentUrl = null;
    });
  }

  private recomputeFilteredGroups(): void {
    const allItems: NotificationItem[] = [];
    const totalPriorities = this.priorities.length;
    const checkedCount = Object.values(this.filterPriorityIds).filter(v => v).length;
    // If all priorities are checked (or none loaded yet), the priority filter is inactive
    const priorityFilterActive = totalPriorities > 0 && checkedCount < totalPriorities;

    const filteredGroups = this.notificationGroups
      .map(g => ({
        ...g,
        items: g.items.filter(i => {
          const typeOk = this._filterType === 'all' || !i.isRead;
          const priorityOk = !priorityFilterActive || this.filterPriorityIds[i.priorityId!] === true;

          const checkboxActive = this.filterCategoryPolicies || this.filterCategoryQuotes ||
            this.filterCategoryClaims || this.filterCategoryPromotions || this.filterCategoryChats;
          let categoryOk: boolean;
          if (checkboxActive) {
            const t = (i.title ?? '').toLowerCase(), m = (i.message ?? '').toLowerCase();
            categoryOk =
              (this.filterCategoryPolicies  && (t.includes('polic') || m.includes('polic'))) ||
              (this.filterCategoryQuotes    && (t.includes('quote') || m.includes('quote'))) ||
              (this.filterCategoryClaims    && (t.includes('claim') || m.includes('claim'))) ||
              (this.filterCategoryPromotions && (t.includes('promo') || m.includes('promo'))) ||
              (this.filterCategoryChats     && (t.includes('chat')  || m.includes('chat')));
          } else {
            const kws: Record<string, string> = { policies: 'polic', quotes: 'quote', claims: 'claim' };
            const kw = kws[this._activeCategory];
            categoryOk = !kw || (i.title ?? '').toLowerCase().includes(kw) || (i.message ?? '').toLowerCase().includes(kw);
          }

          const policyOk = this.policyFilterAll ||
            !Object.values(this.selectedPolicyIds).some(v => v) ||
            (i.policyId != null && this.selectedPolicyIds[i.policyId] === true);

          let dateOk = true;
          if (this.filterDateRange !== 'all' && i.time) {
            const d = new Date(i.time);
            if (!isNaN(d.getTime())) {
              const months = this.filterDateRange === '3m' ? 3 : this.filterDateRange === '6m' ? 6 : 12;
              const cutoff = new Date();
              cutoff.setMonth(cutoff.getMonth() - months);
              dateOk = d >= cutoff;
            }
          }

          return typeOk && priorityOk && categoryOk && policyOk && dateOk;
        })
      }))
      .filter(g => g.items.length > 0);

    for (const g of filteredGroups) { allItems.push(...g.items); }

    const q = this._searchQuery.trim().toLowerCase();
    const searched = q
      ? allItems.filter(i =>
          i.title.toLowerCase().includes(q) ||
          (i.message ?? '').toLowerCase().includes(q)
        )
      : allItems;

    // Build reverse map: threadId → conversation metadata (from notificationGroups)
    const threadConversationMap = new Map<number, { name: string; description?: string }>();
    for (const g of this.notificationGroups) {
      for (const item of g.items) {
        if (!threadConversationMap.has(item.threadId)) {
          threadConversationMap.set(item.threadId, { name: g.label, description: g.description });
        }
      }
    }

    const threadMap = new Map<number, NotificationItem[]>();
    for (const item of searched) {
      const key = item.threadId || 0;
      if (!threadMap.has(key)) threadMap.set(key, []);
      threadMap.get(key)!.push(item);
    }

    const threads: ThreadSummary[] = Array.from(threadMap.entries()).map(([threadId, items]) => {
      const sorted = [...items].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
      const channels = [...new Set(items.map(i => i.channelId).filter(id => id > 0))];
      const conv = threadConversationMap.get(threadId);
      return {
        threadId,
        preview: sorted[sorted.length - 1],
        hasUnread: items.some(i => !i.isRead),
        hasAttachment: items.some(i => !!i.attachmentUrl),
        channels,
        conversationName: conv?.name,
        conversationDescription: conv?.description
      };
    });

    threads.sort((a, b) => new Date(b.preview.time).getTime() - new Date(a.preview.time).getTime());

    const unread = threads.filter(t => t.hasUnread);
    const read   = threads.filter(t => !t.hasUnread);

    this._filteredGroupsByThread = [
      { category: 'unread', label: 'Unread', threads: unread },
      { category: 'read',   label: 'Read',   threads: read   }
    ].filter(g => g.threads.length > 0);

    // Cache derived values so template getters are O(1) on every CD sweep.
    this._unreadCount = unread.length;
    this._allThreadCount = threads.length;

    const policyMap = new Map<string, { subtitle: string; threadIds: Set<number> }>();
    for (const g of this.notificationGroups) {
      for (const item of g.items) {
        if (!item.policyId) continue;
        if (!policyMap.has(item.policyId)) {
          policyMap.set(item.policyId, { subtitle: item.policyLabel ?? '', threadIds: new Set() });
        }
        policyMap.get(item.policyId)!.threadIds.add(item.threadId);
      }
    }
    this._uniquePolicies = Array.from(policyMap.entries()).map(([policyId, v]) => ({
      policyId, label: policyId, subtitle: v.subtitle, count: v.threadIds.size
    }));

    this.globalQuestionService.unreadNotificationCount.next(unread.length);
  }

  private recomputeSelectedMessages(): void {
    if (!this.currentThreadId) { this._selectedThreadMessagesByDate = []; return; }
    const all: NotificationItem[] = [];
    for (const g of this.notificationGroups) {
      all.push(...g.items.filter(i => i.threadId === this.currentThreadId));
    }
    all.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
    const dateMap = new Map<string, NotificationItem[]>();
    for (const m of all) {
      const d = m.time ? new Date(m.time) : null;
      const key = d && !isNaN(d.getTime())
        ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })
        : '';
      if (!dateMap.has(key)) dateMap.set(key, []);
      dateMap.get(key)!.push(m);
    }
    this._selectedThreadMessagesByDate = Array.from(dateMap.entries()).map(([date, messages]) => ({ date, messages }));
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.chatBodyEl?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 0);
  }

  selectNotification(item: NotificationItem): void {
    console.log('selectNotification', item);
    this.selectedNotificationId = item.id;
    this.currentThreadId = item.threadId;
    this.showNewChat = false;
    this.showOlderMessages = false;
    if (!item.isRead) {
      item.isRead = true;
      const params = new Map<string, string>();
      params.set('message_id', item.id);
      params.set('reader_type', this.isBrokerOrAdmin ? 'agent' : 'client');
      this.globalQuestionService.runCommandNew('comm.usp_mark_message_read', params).subscribe();
    }
    this.recomputeSelectedMessages();
    $('body').addClass('notification-active');
    this.scrollToBottom();
    setTimeout(() => { $('[data-toggle="dropdown"]').dropdown(); }, 100);
  }

  clearSelection(): void {
    this.selectedNotificationId = null;
    this.currentThreadId = null;
    this.showNewChat = false;
    this.recomputeSelectedMessages();
    $('body').removeClass('notification-active');
  }

  applyFilters(): void {
    // Ensure at least one priority remains checked (mirrors prototype checkboxSelectLogic).
    const anyPriorityChecked = Object.values(this.filterPriorityIds).some(v => v);
    if (!anyPriorityChecked) this.priorities.forEach(p => this.filterPriorityIds[p.priority_id] = true);
    this.recomputeFilteredGroups();
    this.cdr.detectChanges();
    $('#status-filter-apply').closest('.dropdown').find('[data-toggle="dropdown"]').dropdown('hide');
  }

  clearFilters(): void {
    this._filterType = 'all';
    this.filterCategoryPolicies = false;
    this.filterCategoryQuotes = false;
    this.filterCategoryClaims = false;
    this.filterCategoryPromotions = false;
    this.filterCategoryChats = false;
    this.filterDateRange = 'all';
    this.policyFilterAll = true;
    this.selectedPolicyIds = {};
    this.priorities.forEach(p => this.filterPriorityIds[p.priority_id] = true);
    this.recomputeFilteredGroups();
    this.cdr.detectChanges();
  }

  onPolicyFilterAllChange(): void {
    if (this.policyFilterAll) this.selectedPolicyIds = {};
  }

  onPolicyFilterChange(): void {
    const anySelected = Object.values(this.selectedPolicyIds).some(v => v);
    this.policyFilterAll = !anySelected;
  }

  clearPolicyFilter(): void {
    this.policyFilterAll = true;
    this.selectedPolicyIds = {};
    this.recomputeFilteredGroups();
  }

  applyPolicyFilter(): void {
    this.recomputeFilteredGroups();
  }

  activateReply(): void {
    this.replyActive = true;
  }

  onReplyFocus(): void {
    this.replyActive = true;
  }

  onReplyBlur(): void {
    this.deactivateReply();
  }

  deactivateReply(): void {
    setTimeout(() => {
      if (!this.newChatMessage?.trim() && !this.pendingAttachmentUrl) {
        this.replyActive = false;
      }
    }, 150);
  }

  startNewChat(): void {
    this.showNewChat = true;
    this.selectedNotificationId = null;
    this.currentThreadId = null;
    this.replyActive = false;
    $('body').addClass('notification-active');
  }

  onMessageKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  sendMessage(): void {
    if (!this.newChatMessage?.trim() && !this.pendingAttachmentUrl) return;

    const user = this.userStore.getValue();
    const isBrokerOrAdmin = user?.console_access || user?.broker_id > 0;
    const personId = isBrokerOrAdmin ? this.policy?.person[0]?.person_id : user?.person_id;

    const selectedChannels = Object.entries(this.notifyChannels)
      .filter(([, v]) => v)
      .map(([k]) => k)
      .join(',');

    const parameters = new Map<string, string>();
    parameters.set('person_id', personId?.toString() ?? '0');
    parameters.set('policy_id', this.policy?.policy_id?.toString() ?? '0');
    parameters.set('direction', isBrokerOrAdmin ? 'outbound' : 'inbound');
    parameters.set('body', this.newChatMessage.trim());
    const senderName = `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim();
    if (senderName) parameters.set('sender_name', senderName);
    parameters.set('thread_id', (this.currentThreadId ?? 0).toString());

    let command: string;
    if (selectedChannels) {
      command = 'comm.usp_send_to_channels';
      parameters.set('channel_ids', selectedChannels);
      if (this.pendingAttachmentUrl) {
        parameters.set('resource_id', this.pendingAttachmentUrl);
      }
    } else {
      command = 'comm.usp_insert_message';
      parameters.set('source_channel_id', '7');
      parameters.set('new_message_id', '0');
      if (this.pendingAttachmentUrl) {
        parameters.set('attachment_url', this.pendingAttachmentUrl);
      }
    }

    this.globalQuestionService.runCommandNew(command, parameters)
      .subscribe(() => {
        this.newChatMessage = '';
        this.pendingAttachmentUrl = null;
        this.loadNotifications();
      });
  }

  openAttachment(url: string, name?: string): void {
    this.attachmentTitle = name || 'Attachment';
    this.viewingAttachmentDownloadUrl = url;
    this.http.get(url, { responseType: 'blob' }).subscribe(blob => {
      if (this.blobUrl) URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = URL.createObjectURL(blob);
      this.viewingAttachmentUrl = this.blobUrl;
      $('#attachment-modal').modal('show');
    });
  }

  closeAttachment(): void {
    $('#attachment-modal').modal('hide');
    if (this.blobUrl) { URL.revokeObjectURL(this.blobUrl); this.blobUrl = null; }
    this.viewingAttachmentUrl = null;
    this.viewingAttachmentDownloadUrl = null;
  }

  onSidecolScroll(): void {
    const container = this.sidecolInnerEl?.nativeElement;
    if (!container) return;
    const scrollTop = container.scrollTop;
    const titles = container.querySelectorAll<HTMLElement>('.notification-list__title[data-category]');
    let stuck: string | null = null;
    titles.forEach(title => {
      const li = title.closest('li') as HTMLElement;
      if (li && li.offsetTop <= scrollTop + 1) {
        stuck = title.getAttribute('data-category');
      }
    });
    this.stuckCategory = stuck;
  }

  goBack(): void {
    this.router.navigate([
      this.isBrokerOrAdmin ? RouterLinks.PolicyHolders : RouterLinks.DashboardLanding,
    ]);
  }
}
